// Saudi Dates Data & Utilities
import {
  FaMoneyBillWave,
  FaHeart,
  FaHome,
  FaShieldAlt,
  FaGraduationCap,
  FaGift,
  FaPrayingHands,
  FaFlag,
  FaLandmark,
  FaStar,
  FaCrown,
  FaWallet
} from 'react-icons/fa';
import { IconType } from 'react-icons';
// @ts-expect-error - moment-hijri types
import moment from 'moment-hijri';

// نوع بيانات الموعد
export type SaudiDateType = {
  id: string;
  title: string;
  description: string;
  category: 'salaries' | 'support' | 'education' | 'holidays' | 'services' | 'other';
  icon: IconType;
  color: string;
  darkColor: string;
  bgColor: string;
  borderColor: string;
  getNextDate: () => Date;
};

// دالة مساعدة لتوليد مواعيد متكررة مع التحديث التلقائي
export function recurringDate(day: number, month?: number): () => Date {
  return () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    let nextDate: Date;
    
    if (typeof month === 'number') {
      // مواعيد سنوية ثابتة (مثل الأعياد)
      nextDate = new Date(currentYear, month, day);
      
      // إذا كان الموعد قد مر، انتقل للعام القادم
      if (nextDate <= now) {
        nextDate = new Date(currentYear + 1, month, day);
      }
    } else {
      // مواعيد شهرية متكررة (مثل الرواتب)
      nextDate = new Date(currentYear, currentMonth, day);
      
      // إذا كان الموعد قد مر، انتقل للشهر القادم
      if (nextDate <= now) {
        nextDate = new Date(currentYear, currentMonth + 1, day);
      }
    }
    
    return nextDate;
  };
}

// دالة مساعدة لتوليد مواعيد متكررة مع التحديث التلقائي بعد يوم واحد
export function autoUpdateRecurringDate(day: number, month?: number): () => Date {
  return () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    let nextDate: Date;
    
    if (typeof month === 'number') {
      // مواعيد سنوية ثابتة
      nextDate = new Date(currentYear, month, day);
      
      // إذا كان الموعد قد مر بأكثر من يوم، انتقل للعام القادم
      const oneDayAfter = new Date(nextDate);
      oneDayAfter.setDate(oneDayAfter.getDate() + 1);
      
      if (now > oneDayAfter) {
        nextDate = new Date(currentYear + 1, month, day);
      }
    } else {
      // مواعيد شهرية متكررة
      nextDate = new Date(currentYear, currentMonth, day);
      
      // إذا كان الموعد قد مر بأكثر من يوم، انتقل للشهر القادم
      const oneDayAfter = new Date(nextDate);
      oneDayAfter.setDate(oneDayAfter.getDate() + 1);
      
      if (now > oneDayAfter) {
        nextDate = new Date(currentYear, currentMonth + 1, day);
      }
    }
    
    return nextDate;
  };
}

// دالة مساعدة خاصة للمواعيد الإسلامية (تقريبية)
export function islamicDate(approximateDay: number, approximateMonth: number): () => Date {
  return () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    let nextDate = new Date(currentYear, approximateMonth, approximateDay);
    
    // إذا كان الموعد قد مر بأكثر من يوم، انتقل للعام القادم
    const oneDayAfter = new Date(nextDate);
    oneDayAfter.setDate(oneDayAfter.getDate() + 1);
    
    if (now > oneDayAfter) {
      nextDate = new Date(currentYear + 1, approximateMonth, approximateDay);
    }
    
    return nextDate;
  };
}

// دالة مساعدة خاصة للمواعيد الشهرية مع معالجة نهاية الشهر
export function monthlyDate(day: number): () => Date {
  return () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // إنشاء التاريخ المطلوب للشهر الحالي
    let nextDate = new Date(currentYear, currentMonth, day);
    
    // التحقق من عدد أيام الشهر الحالي
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    if (day > lastDayOfMonth) {
      // إذا كان اليوم المطلوب أكبر من عدد أيام الشهر، استخدم آخر يوم في الشهر
      nextDate = new Date(currentYear, currentMonth, lastDayOfMonth);
    }
    
    // إذا كان الموعد قد مر بأكثر من يوم، انتقل للشهر القادم
    const oneDayAfter = new Date(nextDate);
    oneDayAfter.setDate(oneDayAfter.getDate() + 1);
    
    if (now > oneDayAfter) {
      // حساب الشهر والعام التالي
      let nextMonth = currentMonth + 1;
      let nextYear = currentYear;
      
      // معالجة الانتقال للعام الجديد
      if (nextMonth > 11) {
        nextMonth = 0;
        nextYear++;
      }
      
      // إنشاء التاريخ للشهر القادم
      nextDate = new Date(nextYear, nextMonth, day);
      
      // التحقق من عدد أيام الشهر القادم
      const lastDayOfNextMonth = new Date(nextYear, nextMonth + 1, 0).getDate();
      if (day > lastDayOfNextMonth) {
        nextDate = new Date(nextYear, nextMonth, lastDayOfNextMonth);
      }
    }
    
    return nextDate;
  };
}

// دالة جديدة لحساب المواعيد المستقبلية بدقة
export function getFutureMonthlyDates(startDate: Date, day: number, count: number): Date[] {
  const futureDates: Date[] = [];
  let currentDate = new Date(startDate);
  
  for (let i = 0; i < count; i++) {
    // حساب التاريخ التالي
    const nextDate = new Date(currentDate);
    
    // إضافة شهر واحد بطريقة آمنة
    const currentMonth = nextDate.getMonth();
    const currentYear = nextDate.getFullYear();
    
    let nextMonth = currentMonth + 1;
    let nextYear = currentYear;
    
    // معالجة الانتقال للعام الجديد
    if (nextMonth > 11) {
      nextMonth = 0;
      nextYear++;
    }
    
    // إنشاء التاريخ للشهر التالي
    nextDate.setFullYear(nextYear, nextMonth, day);
    
    // التحقق من عدد أيام الشهر الجديد
    const lastDayOfMonth = new Date(nextYear, nextMonth + 1, 0).getDate();
    if (day > lastDayOfMonth) {
      nextDate.setDate(lastDayOfMonth);
    }
    
    futureDates.push(new Date(nextDate));
    currentDate = nextDate;
  }
  
  return futureDates;
}

// دالة جديدة لحساب المواعيد السنوية المستقبلية بدقة
export function getFutureYearlyDates(startDate: Date, day: number, month: number, count: number): Date[] {
  const futureDates: Date[] = [];
  let currentDate = new Date(startDate);
  
  for (let i = 0; i < count; i++) {
    // حساب التاريخ التالي
    const nextDate = new Date(currentDate);
    const currentYear = nextDate.getFullYear();
    
    // إضافة عام واحد
    nextDate.setFullYear(currentYear + 1, month, day);
    
    // التحقق من عدد أيام الشهر (للشهور التي لها 30 يوم)
    const lastDayOfMonth = new Date(currentYear + 1, month + 1, 0).getDate();
    if (day > lastDayOfMonth) {
      nextDate.setDate(lastDayOfMonth);
    }
    
    futureDates.push(new Date(nextDate));
    currentDate = nextDate;
  }
  
  return futureDates;
}

// دالة مساعدة خاصة للمواعيد السنوية الثابتة
export function yearlyDate(day: number, month: number): () => Date {
  return () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // إنشاء التاريخ المطلوب للعام الحالي
    let nextDate = new Date(currentYear, month, day);
    
    // إذا كان الموعد قد مر بأكثر من يوم، انتقل للعام القادم
    const oneDayAfter = new Date(nextDate);
    oneDayAfter.setDate(oneDayAfter.getDate() + 1);
    
    if (now > oneDayAfter) {
      nextDate = new Date(currentYear + 1, month, day);
    }
    
    return nextDate;
  };
}

// دوال مساعدة للتحويل الهجري
export function gregorianToHijri(date: Date): { year: number; month: number; day: number } {
  const hijriDate = moment(date);
  return {
    year: hijriDate.iYear(),
    month: hijriDate.iMonth() + 1,
    day: hijriDate.iDate()
  };
}

// دالة للحصول على التاريخ الحالي
export function getCurrentDate(): string {
  const now = new Date();
  return now.toLocaleDateString('ar-SA', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// دالة للحصول على التاريخ الهجري
export function getHijriDate(): string {
  const now = moment();
  return now.format('iMMMM iYYYY');
}

export function hijriToGregorian(hijriYear: number, hijriMonth: number, hijriDay: number): Date {
  return moment().iYear(hijriYear).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
}

// دالة خاصة للمواعيد الإسلامية الدقيقة باستخدام moment-hijri
export function islamicDateAccurate(hijriDay: number, hijriMonth: number): () => Date {
  return () => {
    try {
      const now = moment();
      const currentHijriYear = now.iYear();
      
      // إنشاء التاريخ الهجري للعام الحالي
      let targetHijriYear = currentHijriYear;
      let targetDate = moment().iYear(targetHijriYear).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
      
      // التحقق من صحة التاريخ
      if (isNaN(targetDate.getTime())) {
        throw new Error('Invalid hijri date');
      }
      
      // إذا كان الموعد قد مر، انتقل للعام القادم
      const oneDayAfter = moment(targetDate).add(1, 'day');
      
      if (now.isAfter(oneDayAfter)) {
        targetHijriYear = currentHijriYear + 1;
        targetDate = moment().iYear(targetHijriYear).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
        
        // التحقق من صحة التاريخ الجديد
        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid hijri date for next year');
        }
      }
      
      return targetDate;
    } catch (error) {
      console.warn('خطأ في حساب التاريخ الهجري الدقيق، استخدام الدالة البديلة:', error);
      return islamicDateFallback(hijriDay, hijriMonth)();
    }
  };
}

// دالة محسنة لحساب المواعيد الإسلامية باستخدام تقويم إسلامي دقيق
export function islamicDateImproved(hijriDay: number, hijriMonth: number): () => Date {
  return () => {
    try {
      // استخدام moment-hijri إذا كان متاحاً
      if (typeof moment !== 'undefined' && moment.iYear) {
        const now = moment();
        const currentHijriYear = now.iYear();
        
        // إنشاء التاريخ الهجري للعام الحالي
        let targetHijriYear = currentHijriYear;
        let targetDate = moment().iYear(targetHijriYear).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
        
        // التحقق من صحة التاريخ
        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid hijri date from moment-hijri');
        }
        
        // إذا كان الموعد قد مر، انتقل للعام القادم
        const oneDayAfter = moment(targetDate).add(1, 'day');
        
        if (now.isAfter(oneDayAfter)) {
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
          
          // التحقق من صحة التاريخ الجديد
          if (isNaN(targetDate.getTime())) {
            throw new Error('Invalid hijri date for next year from moment-hijri');
          }
        }
        
        // التحقق النهائي: تأكد من أن التاريخ في المستقبل
        const nowDate = new Date();
        if (targetDate.getTime() <= nowDate.getTime()) {
          // إذا كان التاريخ في الماضي، انتقل للعام القادم
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
        }
        
        return targetDate;
      } else {
        throw new Error('moment-hijri not available');
      }
    } catch (error) {
      console.warn('خطأ في حساب التاريخ الهجري المحسن، استخدام الدالة البديلة:', error);
      return islamicDateFallback(hijriDay, hijriMonth)();
    }
  };
}

// دالة محسنة لموعد الحج
export function hajjDateImproved(): () => Date {
  return () => {
    try {
      // استخدام moment-hijri إذا كان متاحاً
      if (typeof moment !== 'undefined' && moment.iYear) {
        const now = moment();
        const currentHijriYear = now.iYear();
        
        // موعد الحج يبدأ من 5 ذو الحجة
        let targetHijriYear = currentHijriYear;
        let targetDate = moment().iYear(targetHijriYear).iMonth(11).iDate(5).toDate(); // 5 ذو الحجة
        
        // التحقق من صحة التاريخ
        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid hajj date from moment-hijri');
        }
        
        // إذا كان الموعد قد مر، انتقل للعام القادم
        const oneDayAfter = moment(targetDate).add(1, 'day');
        
        if (now.isAfter(oneDayAfter)) {
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(11).iDate(5).toDate();
          
          // التحقق من صحة التاريخ الجديد
          if (isNaN(targetDate.getTime())) {
            throw new Error('Invalid hajj date for next year from moment-hijri');
          }
        }
        
        // التحقق النهائي: تأكد من أن التاريخ في المستقبل
        const nowDate = new Date();
        if (targetDate.getTime() <= nowDate.getTime()) {
          // إذا كان التاريخ في الماضي، انتقل للعام القادم
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(11).iDate(5).toDate();
        }
        
        return targetDate;
      } else {
        throw new Error('moment-hijri not available');
      }
    } catch (error) {
      console.warn('خطأ في حساب موعد الحج المحسن، استخدام الدالة البديلة:', error);
      return islamicDateFallback(5, 12)();
    }
  };
}

// دالة بديلة محسنة للمواعيد الإسلامية (في حالة وجود مشاكل مع moment-hijri)
export function islamicDateFallback(hijriDay: number, hijriMonth: number): () => Date {
  return () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // تحويل تقريبي من الهجري إلى الميلادي
    // هذه دالة احتياطية في حالة وجود مشاكل مع moment-hijri
    const gregorianYear = Math.floor((currentYear - 622) * 0.9702 + 622);
    
    // إنشاء التاريخ التقريبي
    let targetDate = new Date(gregorianYear, hijriMonth - 1, hijriDay);
    
    // التحقق من صحة التاريخ
    if (isNaN(targetDate.getTime())) {
      // إذا كان التاريخ غير صحيح، استخدم تاريخ تقريبي آخر
      targetDate = new Date(currentYear, hijriMonth - 1, hijriDay);
    }
    
    // إذا كان الموعد قد مر، انتقل للعام القادم
    const oneDayAfter = new Date(targetDate);
    oneDayAfter.setDate(oneDayAfter.getDate() + 1);
    
    if (now > oneDayAfter) {
      targetDate = new Date(gregorianYear + 1, hijriMonth - 1, hijriDay);
      
      // التحقق من صحة التاريخ الجديد
      if (isNaN(targetDate.getTime())) {
        targetDate = new Date(currentYear + 1, hijriMonth - 1, hijriDay);
      }
    }
    
    // التحقق النهائي: تأكد من أن التاريخ في المستقبل
    if (targetDate.getTime() <= now.getTime()) {
      // إذا كان التاريخ في الماضي، انتقل للعام القادم
      targetDate = new Date(gregorianYear + 1, hijriMonth - 1, hijriDay);
      
      // التحقق من صحة التاريخ الجديد
      if (isNaN(targetDate.getTime())) {
        targetDate = new Date(currentYear + 1, hijriMonth - 1, hijriDay);
      }
    }
    
    return targetDate;
  };
}

// دالة جديدة لحساب عيد الفطر (1 شوال - 3 أيام)
export function eidAlFitrAccurate(): () => Date {
  return () => {
    try {
      // استخدام moment-hijri لحساب دقيق
      if (typeof moment !== 'undefined' && moment.iYear) {
        const now = moment();
        const currentHijriYear = now.iYear();
        
        // عيد الفطر يبدأ في 1 شوال
        let targetHijriYear = currentHijriYear;
        let targetDate = moment().iYear(targetHijriYear).iMonth(9).iDate(1).toDate(); // 1 شوال
        
        // التحقق من صحة التاريخ
        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid Eid Al-Fitr date from moment-hijri');
        }
        
        // إذا كان الموعد قد مر، انتقل للعام القادم
        const oneDayAfter = moment(targetDate).add(1, 'day');
        if (now.isAfter(oneDayAfter)) {
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(9).iDate(1).toDate();
          
          // التحقق من صحة التاريخ الجديد
          if (isNaN(targetDate.getTime())) {
            throw new Error('Invalid Eid Al-Fitr date for next year from moment-hijri');
          }
        }
        
        return targetDate;
      } else {
        // استخدام التاريخ التقريبي كبديل
        const now = new Date();
        const currentYear = now.getFullYear();
        
        // عيد الفطر لعام 2025: 29 مارس 2025
        let targetDate = new Date(currentYear, 2, 29); // 29 مارس
        
        // إذا كان التاريخ في الماضي، انتقل للعام القادم
        if (targetDate.getTime() <= now.getTime()) {
          targetDate = new Date(currentYear + 1, 2, 29);
        }
        
        return targetDate;
      }
    } catch (error) {
      console.warn('خطأ في حساب عيد الفطر، استخدام التاريخ التقريبي:', error);
      const fallbackYear = new Date().getFullYear() + 1;
      return new Date(fallbackYear, 2, 29);
    }
  };
}

// دالة دقيقة لحساب عيد الأضحى (10 ذو الحجة - 3 أيام)
export function eidAlAdhaAccurate(): () => Date {
  return () => {
    try {
      // استخدام moment-hijri لحساب دقيق
      if (typeof moment !== 'undefined' && moment.iYear) {
        const now = moment();
        const currentHijriYear = now.iYear();
        
        // عيد الأضحى يبدأ في 10 ذو الحجة
        let targetHijriYear = currentHijriYear;
        let targetDate = moment().iYear(targetHijriYear).iMonth(11).iDate(10).toDate(); // 10 ذو الحجة
        
        // التحقق من صحة التاريخ
        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid Eid Al-Adha date from moment-hijri');
        }
        
        // إذا كان الموعد قد مر، انتقل للعام القادم
        const oneDayAfter = moment(targetDate).add(1, 'day');
        if (now.isAfter(oneDayAfter)) {
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(11).iDate(10).toDate();
          
          // التحقق من صحة التاريخ الجديد
          if (isNaN(targetDate.getTime())) {
            throw new Error('Invalid Eid Al-Adha date for next year from moment-hijri');
          }
        }
        
        return targetDate;
      } else {
        // استخدام التاريخ التقريبي كبديل
        const now = new Date();
        const currentYear = now.getFullYear();
        
        // عيد الأضحى لعام 2025: 4 يونيو 2025
        let targetDate = new Date(currentYear, 5, 4); // 4 يونيو
        
        // إذا كان التاريخ في الماضي، انتقل للعام القادم
        if (targetDate.getTime() <= now.getTime()) {
          targetDate = new Date(currentYear + 1, 5, 4);
        }
        
        return targetDate;
      }
    } catch (error) {
      console.warn('خطأ في حساب عيد الأضحى، استخدام التاريخ التقريبي:', error);
      const fallbackYear = new Date().getFullYear() + 1;
      return new Date(fallbackYear, 5, 4);
    }
  };
}

// دالة دقيقة لحساب شهر رمضان (1 رمضان - 30 يوم)
export function ramadanAccurate(): () => Date {
  return () => {
    try {
      // استخدام moment-hijri لحساب دقيق
      if (typeof moment !== 'undefined' && moment.iYear) {
        const now = moment();
        const currentHijriYear = now.iYear();
        
        // رمضان يبدأ في 1 رمضان
        let targetHijriYear = currentHijriYear;
        let targetDate = moment().iYear(targetHijriYear).iMonth(8).iDate(1).toDate(); // 1 رمضان
        
        // التحقق من صحة التاريخ
        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid Ramadan date from moment-hijri');
        }
        
        // إذا كان الموعد قد مر، انتقل للعام القادم
        const oneDayAfter = moment(targetDate).add(1, 'day');
        if (now.isAfter(oneDayAfter)) {
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(8).iDate(1).toDate();
          
          // التحقق من صحة التاريخ الجديد
          if (isNaN(targetDate.getTime())) {
            throw new Error('Invalid Ramadan date for next year from moment-hijri');
          }
        }
        
        return targetDate;
      } else {
        // استخدام التاريخ التقريبي كبديل
        const now = new Date();
        const currentYear = now.getFullYear();
        
        // رمضان لعام 2025: 28 فبراير 2025
        let targetDate = new Date(currentYear, 1, 28); // 28 فبراير
        
        // إذا كان التاريخ في الماضي، انتقل للعام القادم
        if (targetDate.getTime() <= now.getTime()) {
          targetDate = new Date(currentYear + 1, 1, 28);
        }
        
        return targetDate;
      }
    } catch (error) {
      console.warn('خطأ في حساب رمضان، استخدام التاريخ التقريبي:', error);
      const fallbackYear = new Date().getFullYear() + 1;
      return new Date(fallbackYear, 1, 28);
    }
  };
}

// دالة دقيقة لحساب الحج (8 ذو الحجة - 5 أيام)
export function hajjAccurate(): () => Date {
  return () => {
    try {
      // استخدام moment-hijri لحساب دقيق
      if (typeof moment !== 'undefined' && moment.iYear) {
        const now = moment();
        const currentHijriYear = now.iYear();
        
        // الحج يبدأ في 8 ذو الحجة
        let targetHijriYear = currentHijriYear;
        let targetDate = moment().iYear(targetHijriYear).iMonth(11).iDate(8).toDate(); // 8 ذو الحجة
        
        // التحقق من صحة التاريخ
        if (isNaN(targetDate.getTime())) {
          throw new Error('Invalid Hajj date from moment-hijri');
        }
        
        // إذا كان الموعد قد مر، انتقل للعام القادم
        const oneDayAfter = moment(targetDate).add(1, 'day');
        if (now.isAfter(oneDayAfter)) {
          targetHijriYear = currentHijriYear + 1;
          targetDate = moment().iYear(targetHijriYear).iMonth(11).iDate(8).toDate();
          
          // التحقق من صحة التاريخ الجديد
          if (isNaN(targetDate.getTime())) {
            throw new Error('Invalid Hajj date for next year from moment-hijri');
          }
        }
        
        return targetDate;
      } else {
        // استخدام التاريخ التقريبي كبديل
        const now = new Date();
        const currentYear = now.getFullYear();
        
        // الحج لعام 2025: 2 يونيو 2025
        let targetDate = new Date(currentYear, 5, 2); // 2 يونيو
        
        // إذا كان التاريخ في الماضي، انتقل للعام القادم
        if (targetDate.getTime() <= now.getTime()) {
          targetDate = new Date(currentYear + 1, 5, 2);
        }
        
        return targetDate;
      }
    } catch (error) {
      console.warn('خطأ في حساب الحج، استخدام التاريخ التقريبي:', error);
      const fallbackYear = new Date().getFullYear() + 1;
      return new Date(fallbackYear, 5, 2);
    }
  };
}

// قائمة المواعيد (قابلة للتعديل والإضافة بسهولة)
export const saudiDates: SaudiDateType[] = [
  {
    id: 'governmentSalary',
    title: 'رواتب القطاع الحكومي',
    description: 'رواتب موظفي القطاع الحكومي',
    category: 'salaries',
    icon: FaMoneyBillWave,
    color: 'from-green-500 to-green-600',
    darkColor: 'dark:from-green-600 dark:to-green-700',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-700',
    getNextDate: monthlyDate(25) // 25 من كل شهر - معالجة نهاية الشهر
  },
  {
    id: 'retiredSalary',
    title: 'رواتب المتقاعدين',
    description: 'رواتب المتقاعدين',
    category: 'salaries',
    icon: FaHeart,
    color: 'from-pink-500 to-pink-600',
    darkColor: 'dark:from-pink-600 dark:to-pink-700',
    bgColor: 'bg-pink-50 dark:bg-pink-900/20',
    borderColor: 'border-pink-200 dark:border-pink-700',
    getNextDate: monthlyDate(3) // 3 من كل شهر - معالجة نهاية الشهر
  },
  {
    id: 'housingSupport',
    title: 'الدعم السكني',
    description: 'دفعات الدعم السكني',
    category: 'support',
    icon: FaHome,
    color: 'from-blue-500 to-blue-600',
    darkColor: 'dark:from-blue-600 dark:to-blue-700',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20',
    borderColor: 'border-blue-200 dark:border-blue-700',
    getNextDate: monthlyDate(15) // 15 من كل شهر - معالجة نهاية الشهر
  },
  {
    id: 'socialSecurity',
    title: 'الضمان الاجتماعي',
    description: 'مخصصات الضمان الاجتماعي',
    category: 'support',
    icon: FaShieldAlt,
    color: 'from-purple-500 to-purple-600',
    darkColor: 'dark:from-purple-600 dark:to-purple-700',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    borderColor: 'border-purple-200 dark:border-purple-700',
    getNextDate: monthlyDate(1) // 1 من كل شهر - معالجة نهاية الشهر
  },
  {
    id: 'academicYear',
    title: 'بداية العام الدراسي',
    description: 'بداية العام الدراسي الجديد',
    category: 'education',
    icon: FaGraduationCap,
    color: 'from-indigo-500 to-indigo-600',
    darkColor: 'dark:from-indigo-600 dark:to-indigo-700',
    bgColor: 'bg-indigo-50 dark:bg-indigo-900/20',
    borderColor: 'border-indigo-200 dark:border-indigo-700',
    getNextDate: yearlyDate(1, 8) // 1 سبتمبر من كل عام
  },
  {
    id: 'eidAlFitr',
    title: 'عيد الفطر',
    description: 'عيد الفطر المبارك - 1 شوال (3 أيام)',
    category: 'holidays',
    icon: FaGift,
    color: 'from-yellow-500 to-yellow-600',
    darkColor: 'dark:from-yellow-600 dark:to-yellow-700',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-700',
    getNextDate: eidAlFitrAccurate()
  },
  {
    id: 'eidAlAdha',
    title: 'عيد الأضحى',
    description: 'عيد الأضحى المبارك - 10 ذو الحجة (3 أيام)',
    category: 'holidays',
    icon: FaPrayingHands,
    color: 'from-red-500 to-red-600',
    darkColor: 'dark:from-red-600 dark:to-red-700',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-700',
    getNextDate: eidAlAdhaAccurate()
  },
  {
    id: 'nationalDay',
    title: 'اليوم الوطني',
    description: 'اليوم الوطني السعودي',
    category: 'holidays',
    icon: FaFlag,
    color: 'from-green-500 to-green-600',
    darkColor: 'dark:from-green-600 dark:to-green-700',
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-700',
    getNextDate: yearlyDate(23, 8) // 23 سبتمبر من كل عام
  },
  {
    id: 'foundationDay',
    title: 'يوم التأسيس',
    description: 'يوم التأسيس السعودي',
    category: 'holidays',
    icon: FaLandmark,
    color: 'from-orange-500 to-orange-600',
    darkColor: 'dark:from-orange-600 dark:to-orange-700',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-200 dark:border-orange-700',
    getNextDate: yearlyDate(22, 1) // 22 فبراير من كل عام
  },
  {
    id: 'ramadan',
    title: 'شهر رمضان',
    description: 'شهر رمضان المبارك - 1 رمضان (30 يوم)',
    category: 'holidays',
    icon: FaStar,
    color: 'from-purple-500 to-purple-600',
    darkColor: 'dark:from-purple-600 dark:to-purple-700',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20',
    borderColor: 'border-purple-200 dark:border-purple-700',
    getNextDate: ramadanAccurate()
  },
  {
    id: 'hajj',
    title: 'الحج',
    description: 'موسم الحج - 8 ذو الحجة (5 أيام)',
    category: 'holidays',
    icon: FaCrown,
    color: 'from-yellow-500 to-yellow-600',
    darkColor: 'dark:from-yellow-600 dark:to-yellow-700',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-700',
    getNextDate: hajjAccurate()
  },
  {
    id: 'citizenAccount',
    title: 'إيداع حساب المواطن',
    description: 'إيداع حساب المواطن',
    category: 'support',
    icon: FaWallet,
    color: 'from-teal-500 to-teal-600',
    darkColor: 'dark:from-teal-600 dark:to-teal-700',
    bgColor: 'bg-teal-50 dark:bg-teal-900/20',
    borderColor: 'border-teal-200 dark:border-teal-700',
    getNextDate: monthlyDate(10) // 10 من كل شهر - معالجة نهاية الشهر
  }
];

// دالة تحقق من صحة المواعيد (للاستخدام في التطوير)
export function validateSaudiDates(dates: SaudiDateType[]): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  
  for (const date of dates) {
    // التحقق من عدم تكرار المعرف
    if (ids.has(date.id)) {
      errors.push(`Duplicate id: ${date.id}`);
    }
    ids.add(date.id);
    
    // التحقق من صحة التاريخ
    const next = date.getNextDate();
    if (!(next instanceof Date) || isNaN(next.getTime())) {
      errors.push(`Invalid date for: ${date.id}`);
    } else {
      // التحقق من أن التاريخ في المستقبل أو اليوم الحالي
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextDate = new Date(next);
      nextDate.setHours(0, 0, 0, 0);
      
      if (nextDate < today) {
        errors.push(`Date is in the past for: ${date.id} (${nextDate.toDateString()})`);
      }
    }
  }
  
  return errors;
}

// دالة اختبار للتحديث التلقائي (يمكن استخدامها للتحقق من صحة المواعيد)
export function testAutoUpdate() {
  console.log('=== اختبار التحديث التلقائي للمواعيد ===');
  
  // اختبار المواعيد الشهرية
  console.log('\n--- المواعيد الشهرية ---');
  const monthlyTests = [
    { name: 'رواتب الحكومة (25)', func: monthlyDate(25) },
    { name: 'رواتب المتقاعدين (3)', func: monthlyDate(3) },
    { name: 'الدعم السكني (15)', func: monthlyDate(15) },
    { name: 'الضمان الاجتماعي (1)', func: monthlyDate(1) },
    { name: 'حساب المواطن (10)', func: monthlyDate(10) }
  ];
  
  monthlyTests.forEach(test => {
    const date = test.func();
    console.log(`${test.name}: ${date.toLocaleDateString('ar-SA')}`);
  });
  
  // اختبار المواعيد السنوية
  console.log('\n--- المواعيد السنوية ---');
  const yearlyTests = [
    { name: 'بداية العام الدراسي (1 سبتمبر)', func: yearlyDate(1, 8) },
    { name: 'اليوم الوطني (23 سبتمبر)', func: yearlyDate(23, 8) },
    { name: 'يوم التأسيس (22 فبراير)', func: yearlyDate(22, 1) }
  ];
  
  yearlyTests.forEach(test => {
    const date = test.func();
    console.log(`${test.name}: ${date.toLocaleDateString('ar-SA')}`);
  });
  
  // اختبار المواعيد الإسلامية الدقيقة
  console.log('\n--- المواعيد الإسلامية الدقيقة ---');
  const islamicTests = [
    { name: 'عيد الفطر (1 شوال)', func: () => {
      try {
        return islamicDateAccurate(1, 10)();
      } catch (error) {
        return islamicDateFallback(1, 10)();
      }
    }},
    { name: 'عيد الأضحى (10 ذو الحجة)', func: () => {
      try {
        return islamicDateAccurate(10, 12)();
      } catch (error) {
        return islamicDateFallback(10, 12)();
      }
    }},
    { name: 'رمضان (1 رمضان)', func: () => {
      try {
        return islamicDateAccurate(1, 9)();
      } catch (error) {
        return islamicDateFallback(1, 9)();
      }
    }},
    { name: 'الحج (5 ذو الحجة)', func: () => {
      try {
        return hajjDateImproved()();
      } catch (error) {
        return islamicDateFallback(5, 12)();
      }
    }}
  ];
  
  islamicTests.forEach(test => {
    try {
      const date = test.func();
      const hijriDate = gregorianToHijri(date);
      console.log(`${test.name}: ${date.toLocaleDateString('ar-SA')} (${hijriDate.day}/${hijriDate.month}/${hijriDate.year} هـ)`);
    } catch (error) {
      console.error(`❌ خطأ في ${test.name}:`, error);
    }
  });
  
  // اختبار معالجة نهاية الشهر
  console.log('\n--- اختبار معالجة نهاية الشهر ---');
  const endOfMonthTests = [
    { name: '31 يناير', func: monthlyDate(31) },
    { name: '30 فبراير', func: monthlyDate(30) },
    { name: '31 أبريل', func: monthlyDate(31) }
  ];
  
  endOfMonthTests.forEach(test => {
    const date = test.func();
    console.log(`${test.name}: ${date.toLocaleDateString('ar-SA')}`);
  });
  
  // اختبار التحويل الهجري
  console.log('\n--- اختبار التحويل الهجري ---');
  const today = new Date();
  const hijriToday = gregorianToHijri(today);
  console.log(`التاريخ الميلادي: ${today.toLocaleDateString('ar-SA')}`);
  console.log(`التاريخ الهجري: ${hijriToday.day}/${hijriToday.month}/${hijriToday.year} هـ`);
  
  // اختبار المواعيد الشهرية
  console.log('\n--- اختبار المواعيد الشهرية ---');
  testMonthlyDates();
  
  // اختبار دقة المواعيد الإسلامية
  console.log('\n--- اختبار دقة المواعيد الإسلامية ---');
  const eidAlFitrDate = islamicDateAccurate(1, 10)();
  const eidAlAdhaDate = islamicDateAccurate(10, 12)();
  const ramadanDate = islamicDateAccurate(1, 9)();
  const hajjDateResult = hajjDateImproved()();
  
  const accuracyTests = [
    { name: 'عيد الفطر', date: eidAlFitrDate, expected: { day: 1, month: 10 } },
    { name: 'عيد الأضحى', date: eidAlAdhaDate, expected: { day: 10, month: 12 } },
    { name: 'رمضان', date: ramadanDate, expected: { day: 1, month: 9 } },
    { name: 'الحج', date: hajjDateResult, expected: { day: 5, month: 12 } }
  ];
  
  accuracyTests.forEach(test => {
    const hijriResult = gregorianToHijri(test.date);
    const isAccurate = hijriResult.day === test.expected.day && hijriResult.month === test.expected.month;
    console.log(`${test.name}: ${hijriResult.day}/${hijriResult.month}/${hijriResult.year} هـ ${isAccurate ? '✅' : '❌'}`);
  });
  
  console.log('\n=== انتهى الاختبار ===');
}

// دالة تحقق خاصة للمواعيد الإسلامية
export function validateIslamicDates(): string[] {
  const errors: string[] = [];
  
  const islamicDates = [
    { id: 'eidAlFitr', name: 'عيد الفطر', day: 1, month: 10 },
    { id: 'eidAlAdha', name: 'عيد الأضحى', day: 10, month: 12 },
    { id: 'ramadan', name: 'رمضان', day: 1, month: 9 },
    { id: 'hajj', name: 'الحج', day: 5, month: 12 }
  ];
  
  islamicDates.forEach(date => {
    try {
      // اختبار الدالة الرئيسية
      const mainResult = islamicDateAccurate(date.day, date.month)();
      const mainHijri = gregorianToHijri(mainResult);
      
      // التحقق من صحة التاريخ الهجري
      if (mainHijri.day !== date.day || mainHijri.month !== date.month) {
        errors.push(`${date.name}: التاريخ الهجري غير صحيح (متوقع: ${date.day}/${date.month}، فعلي: ${mainHijri.day}/${mainHijri.month})`);
      }
      
      // التحقق من أن التاريخ في المستقبل
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextDate = new Date(mainResult);
      nextDate.setHours(0, 0, 0, 0);
      
      if (nextDate < today) {
        errors.push(`${date.name}: التاريخ في الماضي (${mainResult.toDateString()})`);
      }
      
    } catch (error) {
      errors.push(`${date.name}: خطأ في الحساب - ${error}`);
    }
  });
  
  return errors;
}

// دالة اختبار خاصة للمواعيد الإسلامية
export function testIslamicDates() {
  console.log('=== اختبار المواعيد الإسلامية ===');
  
  const islamicDates = [
    { id: 'eidAlFitr', name: 'عيد الفطر', day: 1, month: 10 },
    { id: 'eidAlAdha', name: 'عيد الأضحى', day: 10, month: 12 },
    { id: 'ramadan', name: 'رمضان', day: 1, month: 9 },
    { id: 'hajj', name: 'الحج', day: 5, month: 12 }
  ];
  
  islamicDates.forEach(date => {
    try {
      // اختبار الدالة الرئيسية
      const mainResult = islamicDateAccurate(date.day, date.month)();
      const mainHijri = gregorianToHijri(mainResult);
      
      // اختبار الدالة البديلة
      const fallbackResult = islamicDateFallback(date.day, date.month)();
      
      console.log(`\n${date.name}:`);
      console.log(`  الدالة الرئيسية: ${mainResult.toLocaleDateString('ar-SA')} (${mainHijri.day}/${mainHijri.month}/${mainHijri.year} هـ)`);
      console.log(`  الدالة البديلة: ${fallbackResult.toLocaleDateString('ar-SA')}`);
      
      // التحقق من صحة التاريخ الهجري
      const isAccurate = mainHijri.day === date.day && mainHijri.month === date.month;
      console.log(`  الدقة: ${isAccurate ? '✅' : '❌'}`);
      
    } catch (error) {
      console.error(`❌ خطأ في اختبار ${date.name}:`, error);
    }
  });
  
  console.log('\n=== انتهى اختبار المواعيد الإسلامية ===');
}

// دالة اختبار خاصة للمواعيد الشهرية
export function testMonthlyDates() {
  console.log('=== اختبار المواعيد الشهرية ===');
  
  const monthlyTests = [
    { name: 'رواتب الحكومة (25)', func: monthlyDate(25) },
    { name: 'رواتب المتقاعدين (3)', func: monthlyDate(3) },
    { name: 'الدعم السكني (15)', func: monthlyDate(15) },
    { name: 'الضمان الاجتماعي (1)', func: monthlyDate(1) },
    { name: 'حساب المواطن (10)', func: monthlyDate(10) }
  ];
  
  monthlyTests.forEach(test => {
    try {
      const date = test.func();
      console.log(`${test.name}: ${date.toLocaleDateString('ar-SA')} (${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()})`);
      
      // التحقق من صحة التاريخ
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      if (date.getDate() > lastDayOfMonth) {
        console.error(`❌ خطأ: التاريخ غير صحيح لـ ${test.name}`);
      } else {
        console.log(`✅ التاريخ صحيح لـ ${test.name}`);
      }
    } catch (error) {
      console.error(`❌ خطأ في اختبار ${test.name}:`, error);
    }
  });
  
  // اختبار معالجة نهاية الشهر
  console.log('\n--- اختبار معالجة نهاية الشهر ---');
  const endOfMonthTests = [
    { name: '31 يناير', func: monthlyDate(31) },
    { name: '30 فبراير', func: monthlyDate(30) },
    { name: '31 أبريل', func: monthlyDate(31) },
    { name: '31 يونيو', func: monthlyDate(31) },
    { name: '31 سبتمبر', func: monthlyDate(31) },
    { name: '31 نوفمبر', func: monthlyDate(31) }
  ];
  
  endOfMonthTests.forEach(test => {
    try {
      const date = test.func();
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
      console.log(`${test.name}: ${date.toLocaleDateString('ar-SA')} (آخر يوم في الشهر: ${lastDayOfMonth})`);
      
      if (date.getDate() <= lastDayOfMonth) {
        console.log(`✅ معالجة نهاية الشهر صحيحة لـ ${test.name}`);
      } else {
        console.error(`❌ خطأ في معالجة نهاية الشهر لـ ${test.name}`);
      }
    } catch (error) {
      console.error(`❌ خطأ في اختبار ${test.name}:`, error);
    }
  });
  
  console.log('\n=== انتهى اختبار المواعيد الشهرية ===');
}

// دالة اختبار خاصة لفحص مشكلة عداد المواعيد الإسلامية
export function testIslamicCountdown() {
  console.log('=== اختبار عداد المواعيد الإسلامية ===');
  
  const islamicDates = [
    { id: 'ramadan', name: 'شهر رمضان', day: 1, month: 9 },
    { id: 'eidAlAdha', name: 'عيد الأضحى', day: 10, month: 12 },
    { id: 'hajj', name: 'الحج', day: 5, month: 12 }
  ];
  
  islamicDates.forEach(date => {
    try {
      console.log(`\n--- اختبار ${date.name} ---`);
      
      // اختبار الدالة الرئيسية
      const mainResult = islamicDateAccurate(date.day, date.month)();
      console.log(`الدالة الرئيسية: ${mainResult.toLocaleDateString('ar-SA')}`);
      console.log(`Timestamp: ${mainResult.getTime()}`);
      console.log(`Valid Date: ${!isNaN(mainResult.getTime())}`);
      
      // اختبار الدالة البديلة
      const fallbackResult = islamicDateFallback(date.day, date.month)();
      console.log(`الدالة البديلة: ${fallbackResult.toLocaleDateString('ar-SA')}`);
      console.log(`Timestamp: ${fallbackResult.getTime()}`);
      console.log(`Valid Date: ${!isNaN(fallbackResult.getTime())}`);
      
      // اختبار حساب الوقت المتبقي
      const now = new Date();
      const difference = mainResult.getTime() - now.getTime();
      console.log(`الفرق بالمللي ثانية: ${difference}`);
      console.log(`الفرق بالأيام: ${Math.floor(difference / (1000 * 60 * 60 * 24))}`);
      
      // اختبار moment-hijri
      try {
        const hijriDate = gregorianToHijri(mainResult);
        console.log(`التاريخ الهجري: ${hijriDate.day}/${hijriDate.month}/${hijriDate.year} هـ`);
      } catch (error) {
        console.error('خطأ في تحويل التاريخ الهجري:', error);
      }
      
    } catch (error) {
      console.error(`❌ خطأ في اختبار ${date.name}:`, error);
    }
  });
  
  // اختبار خاص للحج
  try {
    console.log('\n--- اختبار خاص للحج ---');
    const hajjResult = hajjDateImproved()();
    console.log(`موعد الحج: ${hajjResult.toLocaleDateString('ar-SA')}`);
    console.log(`Timestamp: ${hajjResult.getTime()}`);
    console.log(`Valid Date: ${!isNaN(hajjResult.getTime())}`);
    
    const now = new Date();
    const difference = hajjResult.getTime() - now.getTime();
    console.log(`الفرق بالمللي ثانية: ${difference}`);
    console.log(`الفرق بالأيام: ${Math.floor(difference / (1000 * 60 * 60 * 24))}`);
  } catch (error) {
    console.error('❌ خطأ في اختبار الحج:', error);
  }
  
  console.log('\n=== انتهى اختبار عداد المواعيد الإسلامية ===');
}

// دالة اختبار إضافية لفحص مشكلة العداد بشكل أكثر تفصيلاً
export function debugIslamicCountdown() {
  console.log('=== فحص تفصيلي لعداد المواعيد الإسلامية ===');
  
  const islamicDates = [
    { id: 'ramadan', name: 'شهر رمضان', day: 1, month: 9 },
    { id: 'eidAlAdha', name: 'عيد الأضحى', day: 10, month: 12 },
    { id: 'hajj', name: 'الحج', day: 5, month: 12 }
  ];
  
  islamicDates.forEach(date => {
    console.log(`\n🔍 فحص ${date.name}:`);
    
    // فحص moment-hijri
    try {
      if (typeof moment !== 'undefined') {
        console.log('✅ moment متاح');
        if (moment.iYear) {
          console.log('✅ moment-hijri متاح');
          const now = moment();
          console.log(`التاريخ الحالي: ${now.format('YYYY-MM-DD')}`);
          console.log(`السنة الهجرية الحالية: ${now.iYear()}`);
          
          // اختبار إنشاء تاريخ هجري
          const testDate = moment().iYear(now.iYear()).iMonth(date.month - 1).iDate(date.day);
          console.log(`تاريخ الاختبار: ${testDate.format('YYYY-MM-DD')}`);
          console.log(`Timestamp: ${testDate.valueOf()}`);
          console.log(`Valid: ${!isNaN(testDate.valueOf())}`);
        } else {
          console.log('❌ moment-hijri غير متاح');
        }
      } else {
        console.log('❌ moment غير متاح');
      }
    } catch (error) {
      console.error('❌ خطأ في فحص moment-hijri:', error);
    }
    
    // فحص الدوال المختلفة
    const functions = [
      { name: 'islamicDateImproved', func: () => islamicDateImproved(date.day, date.month)() },
      { name: 'islamicDateAccurate', func: () => islamicDateAccurate(date.day, date.month)() },
      { name: 'islamicDateFallback', func: () => islamicDateFallback(date.day, date.month)() }
    ];
    
    functions.forEach(({ name, func }) => {
      try {
        const result = func();
        console.log(`\n📅 ${name}:`);
        console.log(`  التاريخ: ${result.toLocaleDateString('ar-SA')}`);
        console.log(`  Timestamp: ${result.getTime()}`);
        console.log(`  Valid: ${!isNaN(result.getTime())}`);
        
        // حساب الوقت المتبقي
        const now = new Date();
        const diff = result.getTime() - now.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        console.log(`  الوقت المتبقي: ${days} يوم`);
        console.log(`  في الماضي: ${diff < 0}`);
        
      } catch (error) {
        console.error(`❌ خطأ في ${name}:`, error);
      }
    });
  });
  
  // فحص خاص للحج
  console.log('\n🔍 فحص خاص للحج:');
  try {
    const hajjResult = hajjDateImproved()();
    console.log(`موعد الحج: ${hajjResult.toLocaleDateString('ar-SA')}`);
    console.log(`Timestamp: ${hajjResult.getTime()}`);
    console.log(`Valid: ${!isNaN(hajjResult.getTime())}`);
    
    const now = new Date();
    const diff = hajjResult.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    console.log(`الوقت المتبقي: ${days} يوم`);
    console.log(`في الماضي: ${diff < 0}`);
  } catch (error) {
    console.error('❌ خطأ في فحص الحج:', error);
  }
  
  console.log('\n=== انتهى الفحص التفصيلي ===');
}

// دالة اختبار خاصة لفحص مشكلة إخفاء البطاقات الإسلامية
export function testIslamicCardsVisibility() {
  console.log('=== اختبار ظهور البطاقات الإسلامية ===');
  
  const islamicDateIds = ['ramadan', 'eidAlFitr', 'eidAlAdha', 'hajj'];
  
  islamicDateIds.forEach(id => {
    const dateItem = saudiDates.find(d => d.id === id);
    if (dateItem) {
      try {
        console.log(`\n🔍 فحص ${dateItem.title}:`);
        
        // اختبار الدالة
        const targetDate = dateItem.getNextDate();
        const now = new Date();
        const difference = targetDate.getTime() - now.getTime();
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        
        console.log(`  التاريخ المستهدف: ${targetDate.toLocaleDateString('ar-SA')}`);
        console.log(`  التاريخ الحالي: ${now.toLocaleDateString('ar-SA')}`);
        console.log(`  الفرق بالأيام: ${days}`);
        console.log(`  في الماضي: ${difference < 0}`);
        console.log(`  سيتم إخفاؤها: ${difference < 0}`);
        
        // محاكاة دالة calculateTimeRemaining
        if (difference <= 0) {
          console.log(`  ❌ البطاقة ستُخفى لأن التاريخ في الماضي`);
        } else {
          console.log(`  ✅ البطاقة ستظهر لأن التاريخ في المستقبل`);
        }
        
        // فحص إضافي للتواريخ الهجرية
        try {
          const hijriDate = gregorianToHijri(targetDate);
          console.log(`  التاريخ الهجري: ${hijriDate.day}/${hijriDate.month}/${hijriDate.year} هـ`);
        } catch (error) {
          console.error(`  خطأ في تحويل التاريخ الهجري:`, error);
        }
        
      } catch (error) {
        console.error(`❌ خطأ في فحص ${dateItem.title}:`, error);
      }
    }
  });
  
  console.log('\n=== انتهى اختبار ظهور البطاقات ===');
}

/**
 * دليل إضافة موعد جديد:
 * 
 * 1. أضف عنصر جديد إلى مصفوفة saudiDates بنفس البنية.
 * 2. استخدم الدوال المساعدة:
 *    - autoUpdateRecurringDate(day) للمواعيد الشهرية
 *    - autoUpdateRecurringDate(day, month) للمواعيد السنوية
 *    - islamicDate(day, month) للمواعيد الإسلامية التقريبية
 * 3. تحقق من صحة المواعيد عبر validateSaudiDates.
 * 4. اختبر التحديث التلقائي عبر testAutoUpdate.
 * 
 * مثال:
 * {
 *   id: 'newDate',
 *   title: 'موعد جديد',
 *   description: 'وصف الموعد',
 *   category: 'support',
 *   icon: FaIcon,
 *   color: 'from-color-500 to-color-600',
 *   darkColor: 'dark:from-color-600 dark:to-color-700',
 *   bgColor: 'bg-color-50 dark:bg-color-900/20',
 *   borderColor: 'border-color-200 dark:border-color-700',
 *   getNextDate: autoUpdateRecurringDate(15) // تحديث تلقائي في اليوم 15 من كل شهر
 * }
 */ 

// دالة اختبار شاملة ودقيقة لفحص مشكلة عداد المواعيد الإسلامية
export function comprehensiveIslamicTest() {
  console.log('🔍 === اختبار شامل ودقيق للمواعيد الإسلامية ===');
  
  const islamicDateIds = ['ramadan', 'eidAlFitr', 'eidAlAdha', 'hajj'];
  
  interface TestResult {
    id: string;
    title: string;
    targetDate?: string;
    difference?: number;
    error?: string;
    isWorking: boolean;
    isInFuture: boolean;
    willBeHidden: boolean;
  }
  
  const results = {
    total: islamicDateIds.length,
    working: 0,
    broken: 0,
    hidden: 0,
    details: [] as TestResult[]
  };
  
  islamicDateIds.forEach(id => {
    const dateItem = saudiDates.find(d => d.id === id);
    if (dateItem) {
      console.log(`\n📅 فحص ${dateItem.title} (${id}):`);
      
      try {
        // اختبار الدالة
        const targetDate = dateItem.getNextDate();
        const now = new Date();
        const difference = targetDate.getTime() - now.getTime();
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        
        console.log(`  📆 التاريخ المستهدف: ${targetDate.toLocaleDateString('ar-SA')}`);
        console.log(`  📅 التاريخ الحالي: ${now.toLocaleDateString('ar-SA')}`);
        console.log(`  ⏰ الفرق بالأيام: ${days}`);
        console.log(`  🔍 في الماضي: ${difference < 0 ? 'نعم' : 'لا'}`);
        console.log(`  ✅ صحة التاريخ: ${!isNaN(targetDate.getTime()) ? 'صحيح' : 'غير صحيح'}`);
        
        // فحص إضافي للتواريخ الهجرية
        try {
          const hijriDate = gregorianToHijri(targetDate);
          console.log(`  🌙 التاريخ الهجري: ${hijriDate.day}/${hijriDate.month}/${hijriDate.year} هـ`);
        } catch (error) {
          console.error(`  ❌ خطأ في تحويل التاريخ الهجري:`, error);
        }
        
        // تحليل النتيجة
        const isWorking = !isNaN(targetDate.getTime());
        const isInFuture = difference > 0;
        const willBeHidden = !isInFuture;
        
        if (isWorking && isInFuture) {
          console.log(`  ✅ الحالة: يعمل بشكل صحيح ويظهر`);
          results.working++;
        } else if (isWorking && !isInFuture) {
          console.log(`  ⚠️ الحالة: يعمل لكن مخفي (في الماضي)`);
          results.hidden++;
        } else {
          console.log(`  ❌ الحالة: لا يعمل`);
          results.broken++;
        }
        
        results.details.push({
          id,
          title: dateItem.title,
          targetDate: targetDate.toLocaleDateString('ar-SA'),
          difference: days,
          isWorking,
          isInFuture,
          willBeHidden
        });
        
      } catch (error) {
        console.error(`  ❌ خطأ في فحص ${dateItem.title}:`, error);
        results.broken++;
        results.details.push({
          id,
          title: dateItem.title,
          error: error instanceof Error ? error.message : String(error),
          isWorking: false,
          isInFuture: false,
          willBeHidden: true
        });
      }
    }
  });
  
  // ملخص النتائج
  console.log('\n📊 === ملخص النتائج ===');
  console.log(`إجمالي المواعيد الإسلامية: ${results.total}`);
  console.log(`✅ يعمل ويظهر: ${results.working}`);
  console.log(`⚠️ يعمل لكن مخفي: ${results.hidden}`);
  console.log(`❌ لا يعمل: ${results.broken}`);
  
  if (results.broken > 0 || results.hidden > 0) {
    console.log('\n🔧 === المشاكل المكتشفة ===');
    results.details.forEach(detail => {
      if (!detail.isWorking) {
        console.log(`❌ ${detail.title}: لا يعمل - ${detail.error || 'تاريخ غير صحيح'}`);
      } else if (detail.willBeHidden) {
        console.log(`⚠️ ${detail.title}: مخفي - التاريخ في الماضي (${detail.difference} يوم)`);
      }
    });
  }
  
  console.log('\n=== انتهى الاختبار الشامل ===');
  return results;
}

// دالة اختبار شاملة للمواعيد الإسلامية المحدثة
export function testUpdatedIslamicDates() {
  console.log('🧪 === اختبار المواعيد الإسلامية المحدثة ===');
  
  // فحص توفر moment-hijri
  console.log('\n🔍 فحص توفر المكتبات:');
  console.log(`  📚 moment متاح: ${typeof moment !== 'undefined' ? '✅ نعم' : '❌ لا'}`);
  console.log(`  📚 moment-hijri متاح: ${typeof moment !== 'undefined' && moment.iYear ? '✅ نعم' : '❌ لا'}`);
  
  const islamicDates = [
    { id: 'ramadan', name: 'شهر رمضان', expected: '1 رمضان (30 يوم)', hijriMonth: 8, hijriDay: 1 },
    { id: 'eidAlFitr', name: 'عيد الفطر', expected: '1 شوال (3 أيام)', hijriMonth: 9, hijriDay: 1 },
    { id: 'eidAlAdha', name: 'عيد الأضحى', expected: '10 ذو الحجة (3 أيام)', hijriMonth: 11, hijriDay: 10 },
    { id: 'hajj', name: 'الحج', expected: '8 ذو الحجة (5 أيام)', hijriMonth: 11, hijriDay: 8 }
  ];
  
  islamicDates.forEach(({ id, name, expected, hijriMonth, hijriDay }) => {
    const dateItem = saudiDates.find(d => d.id === id);
    if (dateItem) {
      try {
        console.log(`\n📅 === ${name} (${expected}) ===`);
        
        // اختبار الدالة الحالية
        const targetDate = dateItem.getNextDate();
        const now = new Date();
        const difference = targetDate.getTime() - now.getTime();
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        
        console.log(`  📆 التاريخ المستهدف: ${targetDate.toLocaleDateString('ar-SA')}`);
        console.log(`  📅 التاريخ الحالي: ${now.toLocaleDateString('ar-SA')}`);
        console.log(`  ⏰ الفرق بالأيام: ${days}`);
        console.log(`  🔍 في الماضي: ${difference < 0 ? 'نعم' : 'لا'}`);
        console.log(`  ✅ صحة التاريخ: ${!isNaN(targetDate.getTime()) ? 'صحيح' : 'غير صحيح'}`);
        console.log(`  📝 الوصف: ${dateItem.description}`);
        
        // فحص التاريخ الهجري
        try {
          const hijriDate = gregorianToHijri(targetDate);
          console.log(`  🌙 التاريخ الهجري: ${hijriDate.day}/${hijriDate.month}/${hijriDate.year} هـ`);
          
          // التحقق من صحة التاريخ الهجري
          const isCorrectHijriDate = hijriDate.month === hijriMonth && hijriDate.day === hijriDay;
          console.log(`  ✅ التاريخ الهجري صحيح: ${isCorrectHijriDate ? 'نعم' : 'لا'}`);
          
          if (!isCorrectHijriDate) {
            console.log(`  ⚠️ التاريخ المتوقع: ${hijriDay}/${hijriMonth}/${hijriDate.year} هـ`);
          }
        } catch (error) {
          console.error(`  ❌ خطأ في تحويل التاريخ الهجري:`, error);
        }
        
        // اختبار إضافي باستخدام moment-hijri مباشرة
        if (typeof moment !== 'undefined' && moment.iYear) {
          try {
            const nowMoment = moment();
            const currentHijriYear = nowMoment.iYear();
            const directHijriDate = moment().iYear(currentHijriYear).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
            
            console.log(`  🔧 التاريخ المباشر من moment-hijri: ${directHijriDate.toLocaleDateString('ar-SA')}`);
            
            const directDifference = directHijriDate.getTime() - now.getTime();
            const directDays = Math.floor(directDifference / (1000 * 60 * 60 * 24));
            console.log(`  🔧 الفرق المباشر بالأيام: ${directDays}`);
            
            if (directDifference <= 0) {
              const nextYearDate = moment().iYear(currentHijriYear + 1).iMonth(hijriMonth - 1).iDate(hijriDay).toDate();
              console.log(`  🔧 التاريخ للعام القادم: ${nextYearDate.toLocaleDateString('ar-SA')}`);
            }
          } catch (error) {
            console.error(`  ❌ خطأ في الحساب المباشر:`, error);
          }
        }
        
        // تقييم الحالة النهائية
        if (difference > 0 && !isNaN(targetDate.getTime())) {
          console.log(`  ✅ الحالة النهائية: يعمل بشكل صحيح ويظهر`);
        } else if (!isNaN(targetDate.getTime())) {
          console.log(`  ⚠️ الحالة النهائية: يعمل لكن مخفي (في الماضي)`);
        } else {
          console.log(`  ❌ الحالة النهائية: لا يعمل`);
        }
        
      } catch (error) {
        console.error(`  ❌ خطأ في فحص ${name}:`, error);
      }
    }
  });
  
  console.log('\n✅ === انتهى اختبار المواعيد الإسلامية المحدثة ===');
} 

// دالة إصلاح شاملة للمواعيد الإسلامية
export function fixIslamicDates() {
  console.log('🔧 === إصلاح شامل للمواعيد الإسلامية ===');
  
  // فحص توفر moment-hijri
  const hasMomentHijri = typeof moment !== 'undefined' && moment.iYear;
  console.log(`📚 moment-hijri متاح: ${hasMomentHijri ? '✅ نعم' : '❌ لا'}`);
  
  // تحديث قائمة المواعيد باستخدام الدوال الجديدة
  const updatedDates = saudiDates.map(date => {
    if (date.id === 'ramadan') {
      const newDate = { ...date, getNextDate: ramadanAccurate() };
      console.log(`✅ تم تحديث رمضان: ${newDate.description}`);
      return newDate;
    }
    if (date.id === 'eidAlFitr') {
      const newDate = { ...date, getNextDate: eidAlFitrAccurate() };
      console.log(`✅ تم تحديث عيد الفطر: ${newDate.description}`);
      return newDate;
    }
    if (date.id === 'eidAlAdha') {
      const newDate = { ...date, getNextDate: eidAlAdhaAccurate() };
      console.log(`✅ تم تحديث عيد الأضحى: ${newDate.description}`);
      return newDate;
    }
    if (date.id === 'hajj') {
      const newDate = { ...date, getNextDate: hajjAccurate() };
      console.log(`✅ تم تحديث الحج: ${newDate.description}`);
      return newDate;
    }
    return date;
  });
  
  // اختبار سريع للمواعيد المحدثة
  console.log('\n🧪 اختبار سريع للمواعيد المحدثة:');
  const islamicIds = ['ramadan', 'eidAlFitr', 'eidAlAdha', 'hajj'];
  
  islamicIds.forEach(id => {
    const dateItem = updatedDates.find(d => d.id === id);
    if (dateItem) {
      try {
        const targetDate = dateItem.getNextDate();
        const now = new Date();
        const difference = targetDate.getTime() - now.getTime();
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        
        console.log(`  📅 ${dateItem.title}: ${targetDate.toLocaleDateString('ar-SA')} (${days} يوم)`);
        
        if (difference > 0) {
          console.log(`    ✅ يعمل ويظهر`);
        } else {
          console.log(`    ⚠️ في الماضي - سيتم إخفاؤه`);
        }
      } catch (error) {
        console.error(`    ❌ خطأ: ${error}`);
      }
    }
  });
  
  console.log('\n✅ === تم إصلاح المواعيد الإسلامية بنجاح ===');
  console.log('📅 رمضان: 1 رمضان - 30 يوم');
  console.log('📅 عيد الفطر: 1 شوال - 3 أيام');
  console.log('📅 عيد الأضحى: 10 ذو الحجة - 3 أيام');
  console.log('📅 الحج: 8 ذو الحجة - 5 أيام');
  
  return updatedDates;
}

// دالة تطبيق الإصلاحات في الوقت الفعلي
export function applyIslamicFixes() {
  console.log('🚀 === تطبيق إصلاحات المواعيد الإسلامية في الوقت الفعلي ===');
  
  // تطبيق الإصلاحات
  const fixedDates = fixIslamicDates();
  
  // تحديث قائمة المواعيد الأصلية
  // ملاحظة: هذا سيحدث القائمة الأصلية
  islamicIds.forEach(id => {
    const originalIndex = saudiDates.findIndex(d => d.id === id);
    const fixedDate = fixedDates.find(d => d.id === id);
    
    if (originalIndex !== -1 && fixedDate) {
      saudiDates[originalIndex] = fixedDate;
      console.log(`✅ تم تحديث ${fixedDate.title} في القائمة الأصلية`);
    }
  });
  
  console.log('🎉 === تم تطبيق جميع الإصلاحات بنجاح ===');
  
  // اختبار نهائي
  testUpdatedIslamicDates();
  
  return saudiDates;
}

// قائمة معرفات المواعيد الإسلامية
const islamicIds = ['ramadan', 'eidAlFitr', 'eidAlAdha', 'hajj']; 

// دالة اختبار التواريخ الفعلية لعام 2025
export function test2025IslamicDates() {
  console.log('=== اختبار التواريخ الإسلامية لعام 2025 ===');
  
  const expectedDates = [
    { name: 'رمضان', expected: '2025-02-28', hijri: '1 رمضان 1446' },
    { name: 'عيد الفطر', expected: '2025-03-29', hijri: '1 شوال 1446' },
    { name: 'الحج', expected: '2025-06-02', hijri: '8 ذو الحجة 1446' },
    { name: 'عيد الأضحى', expected: '2025-06-04', hijri: '10 ذو الحجة 1446' }
  ];
  
  const islamicIds = ['ramadan', 'eidAlFitr', 'hajj', 'eidAlAdha'];
  
  islamicIds.forEach((id, index) => {
    const dateItem = saudiDates.find(d => d.id === id);
    if (dateItem) {
      try {
        const targetDate = dateItem.getNextDate();
        const now = new Date();
        const difference = targetDate.getTime() - now.getTime();
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        
        const expected = expectedDates[index];
        const actualDate = targetDate.toISOString().split('T')[0];
        
        console.log(`\n📅 ${dateItem.title}:`);
        console.log(`  التاريخ المتوقع: ${expected.expected} (${expected.hijri})`);
        console.log(`  التاريخ الفعلي: ${actualDate}`);
        console.log(`  الفرق بالأيام: ${days}`);
        console.log(`  في المستقبل: ${difference > 0 ? '✅ نعم' : '❌ لا'}`);
        
        if (actualDate === expected.expected) {
          console.log(`  ✅ التاريخ صحيح`);
        } else {
          console.log(`  ❌ التاريخ غير صحيح`);
        }
        
      } catch (error) {
        console.error(`❌ خطأ في اختبار ${dateItem.title}:`, error);
      }
    }
  });
  
  console.log('\n=== انتهى اختبار التواريخ الإسلامية لعام 2025 ===');
}